import * as React from 'react'
import * as Types from '../declarations'
import * as ClientAuthUtil from '../lib/client-auth'
import { Action, ActionType } from './actions'

const STORAGE_KEY = 'primary' // same key the old redux-persist store used

const initialState: Types.AppState = {
	unit: 'metric',
	userAuthStatusIsKnown: false,
	user: undefined,
	editFeedbackSection: undefined,
	signUpOpen: false,
	logInOpen: false,
}

export const appReducer = (
	state: Types.AppState = initialState,
	action: Action,
): Types.AppState => {
	switch (action.type) {
		case ActionType.CHANGE_UNIT:
			return { ...state, unit: action.payload }

		case ActionType.AUTH_STATUS_VERIFIED:
			if (action.isVerified) {
				return {
					...state,
					userAuthStatusIsKnown: true,
					user: action.user ?? state.user,
				}
			}
			// Server says the token is invalid/expired: drop it and any stale
			// user so persisted auth can never keep `authedUser` truthy.
			ClientAuthUtil.removeToken()
			return { ...state, userAuthStatusIsKnown: true, user: undefined }

		case ActionType.LOGIN_SUCCEEDED:
			ClientAuthUtil.saveToken(action.token)
			return { ...state, userAuthStatusIsKnown: true, user: action.user }
		case ActionType.LOGOUT:
			ClientAuthUtil.removeToken()
			return {
				...state,
				userAuthStatusIsKnown: true,
				user: undefined,
			}
		case ActionType.SUGGEST_EDIT_OPEN:
			return { ...state, editFeedbackSection: action.feedbackSection }

		case ActionType.SUGGEST_EDIT_CLOSE:
			return { ...state, editFeedbackSection: undefined }

		case ActionType.OPEN_SIGNUP:
			return { ...state, signUpOpen: true }

		case ActionType.OPEN_LOGIN:
			return { ...state, logInOpen: true }

		case ActionType.CLOSE_SIGNUP:
			return { ...state, signUpOpen: false, logInOpen: false }

		case ActionType.HYDRATE_PERSISTED:
			return { ...state, ...action.persisted }

		default:
			return state
	}
}

interface AppContextValue {
	state: Types.AppState
	dispatch: React.Dispatch<Action>
}

const AppContext = React.createContext<AppContextValue | undefined>(undefined)

export const AppProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [state, dispatch] = React.useReducer(appReducer, initialState)

	// Rehydrate persisted state after mount (mirrors old PersistGate behaviour:
	// first render uses defaults so SSR markup matches, then state is restored).
	// Auth fields are deliberately NOT restored: persisted auth is untrusted
	// until CheckToken's `authCheck` query validates the token cookie.
	React.useEffect(() => {
		try {
			const raw = window.localStorage.getItem(STORAGE_KEY)
			if (raw) {
				const persisted = JSON.parse(raw) as Partial<Types.AppState>
				delete persisted.user
				delete persisted.userAuthStatusIsKnown
				dispatch({
					type: ActionType.HYDRATE_PERSISTED,
					persisted,
				} as Action)
			}
		} catch {
			// corrupted or unavailable storage — start from defaults
		}
	}, [])

	// Persist on every change (old redux-persist persisted the whole state),
	// minus auth fields: `user`/`userAuthStatusIsKnown` must be re-established
	// via server validation (authCheck) on every boot, never trusted from disk.
	React.useEffect(() => {
		try {
			const { user: _user, userAuthStatusIsKnown: _known, ...rest } = state
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rest))
		} catch {
			// storage full/unavailable — non-fatal
		}
	}, [state])

	const value = React.useMemo(() => ({ state, dispatch }), [state])

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export const useAppStore = (): AppContextValue => {
	const ctx = React.useContext(AppContext)
	if (!ctx) throw new Error('useAppStore must be used within AppProvider')
	return ctx
}

// Drop-in replacements for react-redux hooks — selectors keep their
// plain `(state: AppState) => T` signature.
export const useAppSelector = <T,>(selector: (state: Types.AppState) => T): T => {
	const { state } = useAppStore()
	return selector(state)
}

export const useAppDispatch = (): React.Dispatch<Action> => {
	const { dispatch } = useAppStore()
	return dispatch
}

// Legacy helper kept from the old redux store module
export const setMetric = (newMetric: string): Action => ({
	type: ActionType.CHANGE_UNIT,
	payload: newMetric,
})
